import {
  ChangeDetectionStrategy, Component, computed, ElementRef, inject, output, signal, viewChild, viewChildren
} from '@angular/core';

import { isInputOrTextAreaElement, getContentEditableCaretCoords } from './mention-utils';
import { getCaretCoordinates } from './caret-coords';
import { MatList, MatListItem } from '@angular/material/list';

/**
 * Angular Mentions.
 * https://github.com/dmacfarlane/angular-mentions
 *
 * Copyright (c) 2016 Dan MacFarlane
 */
@Component({
    selector: 'xui-mention-list',
    styleUrls: ['./mention-list.component.scss'],
    templateUrl: './mention-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatList, MatListItem]
})
export class MentionListComponent {
  private readonly element = inject(ElementRef);

  readonly labelKey = signal('label');
  readonly itemClick = output<void>();

  readonly list = viewChild.required<ElementRef>('list');
  readonly matListItemRefs = viewChildren('matListItemRef', { read: ElementRef });

  readonly items = signal<any[]>([]);
  readonly activeIndex = signal(0);
  readonly hidden = signal(false);
  readonly dropUp = signal(false);
  readonly styleOff = signal(false);

  readonly activeItem = computed(() => this.items()[this.activeIndex()]);

  private coords: { top: number; left: number } = { top: 0, left: 0 };
  private offset = 0;

  // Position the dropdown using viewport-relative (fixed) coordinates
  position(nativeParentElement: HTMLInputElement, iframe: HTMLIFrameElement = null) {
    if (isInputOrTextAreaElement(nativeParentElement)) {
      // Get caret position relative to the element, then convert to viewport coords
      this.coords = getCaretCoordinates(nativeParentElement, nativeParentElement.selectionStart, null);
      const parentRect = nativeParentElement.getBoundingClientRect();
      this.coords.top = parentRect.top + this.coords.top - nativeParentElement.scrollTop;
      this.coords.left = parentRect.left + this.coords.left - nativeParentElement.scrollLeft;
      this.offset = this.getBlockCursorDimensions(nativeParentElement).height;
    } else if (iframe) {
      const context: { iframe: HTMLIFrameElement, parent: Element } = { iframe: iframe, parent: iframe.offsetParent };
      this.coords = getContentEditableCaretCoords(context);
    } else {
      // For contenteditable, get viewport-relative caret coords from the Selection API
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        let rect = range.getBoundingClientRect();
        // getBoundingClientRect() on collapsed ranges can return a zero-rect
        if (rect.top === 0 && rect.left === 0 && rect.height === 0) {
          const marker = document.createElement('span');
          marker.textContent = '\u200B'; // zero-width space
          range.insertNode(marker);
          rect = marker.getBoundingClientRect();
          marker.parentNode?.removeChild(marker);
          sel.removeAllRanges();
          sel.addRange(range);
        }
        this.coords = { top: rect.top, left: rect.left };
        this.offset = rect.height || parseFloat(window.getComputedStyle(nativeParentElement).lineHeight) || 20;
      }
    }
    // set the default/initial position
    this.positionElement();
  }

  activateNextItem() {
    const nextIndex = Math.min(this.activeIndex() + 1, this.items().length - 1);
    this.activeIndex.set(Math.max(nextIndex, 0));
    this.scrollActiveItemIntoView();
  }

  activatePreviousItem() {
    const prevIndex = Math.max(this.activeIndex() - 1, 0);
    this.activeIndex.set(prevIndex);
    this.scrollActiveItemIntoView();
  }

  // reset for a new mention search
  reset() {
    this.list().nativeElement.scrollTop = 0;
    this.checkBounds();
  }

  private scrollActiveItemIntoView() {
    const itemRefs = this.matListItemRefs();
    const index = this.activeIndex();
    if (itemRefs.length > 0 && index >= 0 && index < itemRefs.length) {
      const el = itemRefs[index].nativeElement;
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  // final positioning is done after the list is shown (and the height and width are known)
  // ensure it's in the page bounds
  private checkBounds() {
    let left = this.coords.left;
    const top = this.coords.top;
    let dropUp = this.dropUp();
    const bounds: DOMRect = this.list().nativeElement.getBoundingClientRect();
    const viewportMargin = 16;

    // if off right of page, align right
    if (bounds.left + bounds.width > window.innerWidth - viewportMargin) {
      left -= bounds.left + bounds.width - window.innerWidth + viewportMargin;
    }
    // if dropdown would extend past the bottom of the viewport (with margin), force dropUp
    if (bounds.bottom + viewportMargin > window.innerHeight) {
      dropUp = true;
    }
    // if top is off page, disable dropUp
    if (bounds.top < 0) {
      dropUp = false;
    }
    // set the revised/final position
    this.positionElement(left, top, dropUp);
  }

  private positionElement(left: number = this.coords.left, top: number = this.coords.top, dropUp: boolean = this.dropUp()) {
    const el: HTMLElement = this.element.nativeElement;
    el.className = dropUp ? 'drop-up' : 'drop-down';
    el.style.position = 'fixed';
    el.style.zIndex = '10000';
    el.style.left = left + 'px';
    if (dropUp) {
      // Position above the caret
      const listHeight = this.list().nativeElement.getBoundingClientRect().height;
      el.style.top = (top - listHeight - 4) + 'px';
    } else {
      // Position below the caret
      el.style.top = (top + this.offset) + 'px';
    }
  }

  private getBlockCursorDimensions(nativeParentElement: HTMLInputElement) {
    const parentStyles = window.getComputedStyle(nativeParentElement);
    return {
      height: parseFloat(parentStyles.lineHeight),
      width: parseFloat(parentStyles.fontSize)
    };
  }
}
